/**
 * gallery-index.js — TFS Wheels Customer Gallery Index
 *
 * Progressive enhancement for the gallery index page:
 *   - On load: populates Year/Make/Model dependent <select> dropdowns from the
 *     fitment endpoints (/api/fitment/years, /api/fitment/makes, /api/fitment/models).
 *   - On load: populates Diameter / Wheel Brand / Wheel Model <select> dropdowns
 *     from /api/gallery/facets (values present in published builds only).
 *   - Reads filters from the URL querystring on load (deep-link support).
 *   - On filter change: fetches GET {api_base}/api/gallery?... and re-renders the
 *     grid client-side via safe DOM construction (no innerHTML with API data).
 *   - Updates the URL querystring via history.pushState so deep links reproduce
 *     the filtered view on reload.
 *   - Renders pagination controls (prev / next / page numbers).
 *
 * Security: all API-sourced strings are set via textContent or validated before
 * use as attribute values (URLs validated to expected CDN origin). No innerHTML
 * with user/API-controlled content.
 *
 * Config: data-api-base on the #tfs-gallery-index container.
 * Default API base: https://tfs-manager-server-production.up.railway.app
 */

(function () {
  'use strict';

  var DEFAULT_API_BASE = 'https://tfs-manager-server-production.up.railway.app';

  /** Allowed URL schemes for image src and page hrefs from the API. */
  var SAFE_IMG_ORIGINS = ['cdn.shopify.com', 'cdn.shopifycdn.com'];
  var PAGE_LIMIT = 24;

  // ── DOM refs ─────────────────────────────────────────────────────────────────
  var container  = document.getElementById('tfs-gallery-index');
  if (!container) return;

  var API_BASE   = (container.getAttribute('data-api-base') || DEFAULT_API_BASE).replace(/\/$/, '');
  var grid       = document.getElementById('tfs-gi-grid');
  var pagination = document.getElementById('tfs-gi-pagination');
  var filterBtn  = document.getElementById('gi-filter-btn');
  var clearBtn   = document.getElementById('gi-clear-btn');

  var inputs = {
    year:        document.getElementById('gi-year'),
    make:        document.getElementById('gi-make'),
    model:       document.getElementById('gi-model'),
    wheel_brand: document.getElementById('gi-wheel-brand'),
    wheel_model: document.getElementById('gi-wheel-model'),
    diameter:    document.getElementById('gi-diameter'),
    fitment:     document.getElementById('gi-fitment'),
  };

  var _currentPage  = 1;
  var _currentTotal = 0;
  var _loading      = false;

  // ── URL helpers ───────────────────────────────────────────────────────────────

  /**
   * Validate that a URL string is a safe image URL (https, known CDN origin).
   * Returns the URL string if safe, null otherwise.
   */
  function safeImgUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
      var u = new URL(raw);
      if (u.protocol !== 'https:') return null;
      var host = u.hostname.toLowerCase();
      for (var i = 0; i < SAFE_IMG_ORIGINS.length; i++) {
        if (host === SAFE_IMG_ORIGINS[i] || host.endsWith('.' + SAFE_IMG_ORIGINS[i])) return raw;
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Return a safe /pages/build?id=<public_id> href for a build card.
   *
   * Accepts a public_id (UUID — alphanumeric with hyphens).
   * Falls back to deriving from entry_url if the public_id is missing.
   * Rejects any other origin or path to prevent open-redirect.
   */
  function safeBuildHref(entryUrl, publicId) {
    // Prefer public_id directly — cleanest and safest
    if (publicId && /^[0-9a-f-]{36}$/.test(publicId)) {
      return '/pages/build?id=' + encodeURIComponent(publicId);
    }
    // Fall back: extract UUID from an absolute or relative builds path
    if (entryUrl && typeof entryUrl === 'string') {
      var path = entryUrl;
      try {
        var u = new URL(entryUrl);
        path = (u.hostname === 'tfswheels.com' || u.hostname === 'www.tfswheels.com') ? u.pathname : '';
      } catch (_) {
        // not a full URL — treat entryUrl as a relative path
      }
      var m = /^\/(?:pages\/)?builds?\/([0-9a-f-]{36})$/.exec(path);
      if (m) return '/pages/build?id=' + encodeURIComponent(m[1]);
    }
    return null;
  }

  // ── Select helpers ────────────────────────────────────────────────────────────

  /**
   * Populate a <select> element with an array of string values.
   * The first option is always "Any" (value="").
   * Restores a previously selected value if it still exists in the new list.
   */
  function populateSelect(el, values, anyLabel) {
    if (!el) return;
    var prev = el.value;
    // Clear all options
    while (el.firstChild) el.removeChild(el.firstChild);
    // Add "Any" option
    var anyOpt = document.createElement('option');
    anyOpt.value = '';
    anyOpt.textContent = anyLabel || 'Any';
    el.appendChild(anyOpt);
    // Add value options
    if (values && values.length) {
      values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = String(v);
        opt.textContent = String(v);
        el.appendChild(opt);
      });
    }
    // Restore previous selection if still valid
    if (prev && values && values.map(String).indexOf(prev) !== -1) {
      el.value = prev;
    }
  }

  /** Mark a select as loading/disabled during a dependent fetch. */
  function setSelectLoading(el, loading) {
    if (!el) return;
    el.disabled = loading;
    if (loading) {
      el.classList.add('gi-select--loading');
    } else {
      el.classList.remove('gi-select--loading');
    }
  }

  /** Reset a select to just the "Any" option and disable it. */
  function resetSelect(el, anyLabel, disable) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    var anyOpt = document.createElement('option');
    anyOpt.value = '';
    anyOpt.textContent = anyLabel || 'Any';
    el.appendChild(anyOpt);
    el.disabled = !!disable;
    el.classList.remove('gi-select--loading');
  }

  // ── Fitment dependent dropdowns ───────────────────────────────────────────────

  function loadYears(restoreYear) {
    var el = inputs.year;
    if (!el) return;
    setSelectLoading(el, true);
    fetch(API_BASE + '/api/fitment/years', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject('HTTP ' + r.status); })
      .then(function (data) {
        populateSelect(el, data.years || [], 'Any year');
        setSelectLoading(el, false);
        if (restoreYear && el.querySelector('option[value="' + restoreYear + '"]')) {
          el.value = restoreYear;
          // Cascade to makes
          loadMakes(restoreYear, null);
        }
      })
      .catch(function () {
        setSelectLoading(el, false);
      });
  }

  function loadMakes(year, restoreMake) {
    var makeSel  = inputs.make;
    var modelSel = inputs.model;
    if (!makeSel) return;

    if (!year) {
      resetSelect(makeSel, 'Any make', true);
      resetSelect(modelSel, 'Any model', true);
      return;
    }

    resetSelect(modelSel, 'Any model', true);
    setSelectLoading(makeSel, true);

    fetch(API_BASE + '/api/fitment/makes?year=' + encodeURIComponent(year), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject('HTTP ' + r.status); })
      .then(function (data) {
        populateSelect(makeSel, data.makes || [], 'Any make');
        setSelectLoading(makeSel, false);
        makeSel.disabled = false;
        if (restoreMake && makeSel.querySelector('option[value="' + restoreMake + '"]')) {
          makeSel.value = restoreMake;
          loadModels(year, restoreMake, null);
        }
      })
      .catch(function () {
        setSelectLoading(makeSel, false);
        makeSel.disabled = false;
      });
  }

  function loadModels(year, make, restoreModel) {
    var modelSel = inputs.model;
    if (!modelSel) return;

    if (!year || !make) {
      resetSelect(modelSel, 'Any model', true);
      return;
    }

    setSelectLoading(modelSel, true);

    fetch(
      API_BASE + '/api/fitment/models?year=' + encodeURIComponent(year) + '&make=' + encodeURIComponent(make),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) { return r.ok ? r.json() : Promise.reject('HTTP ' + r.status); })
      .then(function (data) {
        populateSelect(modelSel, data.models || [], 'Any model');
        setSelectLoading(modelSel, false);
        modelSel.disabled = false;
        if (restoreModel && modelSel.querySelector('option[value="' + restoreModel + '"]')) {
          modelSel.value = restoreModel;
        }
      })
      .catch(function () {
        setSelectLoading(modelSel, false);
        modelSel.disabled = false;
      });
  }

  // ── Gallery facets (Diameter / Wheel Brand / Wheel Model) ─────────────────────

  function loadFacets(restoreDiameter, restoreWheelBrand, restoreWheelModel) {
    fetch(API_BASE + '/api/gallery/facets', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject('HTTP ' + r.status); })
      .then(function (data) {
        var facets = data.facets || data || {};

        var diameters    = facets.diameters    || [];
        var wheel_brands = facets.wheel_brands || [];
        var wheel_models = facets.wheel_models || [];

        populateSelect(inputs.diameter,    diameters,    'Any');
        populateSelect(inputs.wheel_brand, wheel_brands, 'Any');
        populateSelect(inputs.wheel_model, wheel_models, 'Any');

        // Restore QS values
        if (restoreDiameter    && inputs.diameter    && inputs.diameter.querySelector('option[value="' + restoreDiameter + '"]'))    { inputs.diameter.value    = restoreDiameter;    }
        if (restoreWheelBrand  && inputs.wheel_brand && inputs.wheel_brand.querySelector('option[value="' + restoreWheelBrand + '"]'))  { inputs.wheel_brand.value  = restoreWheelBrand;  }
        if (restoreWheelModel  && inputs.wheel_model && inputs.wheel_model.querySelector('option[value="' + restoreWheelModel + '"]'))  { inputs.wheel_model.value  = restoreWheelModel;  }
      })
      .catch(function () {
        // Leave selects with just "Any" option — graceful degradation
      });
  }

  // ── Init: read querystring and populate inputs ────────────────────────────────
  function readQS() {
    var params = new URLSearchParams(window.location.search);
    // For selects we restore after async data loads; for fitment (static) restore now
    var fitmentEl = inputs.fitment;
    if (fitmentEl) fitmentEl.value = params.get('fitment') || '';
    _currentPage = parseInt(params.get('page'), 10) || 1;
    return params;
  }

  // ── Build params from current input values ────────────────────────────────────
  function buildParams(page) {
    var p = {};
    Object.keys(inputs).forEach(function (key) {
      var el = inputs[key];
      if (!el) return;
      var v = el.value.trim();
      if (v) p[key] = v;
    });
    p.page  = page || _currentPage;
    p.limit = PAGE_LIMIT;
    return p;
  }

  // ── Fetch feed ────────────────────────────────────────────────────────────────
  function fetchBuilds(params, cb) {
    var qs = Object.keys(params)
      .filter(function (k) { return params[k] !== '' && params[k] !== undefined; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]); })
      .join('&');
    var url = API_BASE + '/api/gallery?' + qs;

    _loading = true;
    if (grid) grid.setAttribute('aria-busy', 'true');

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        _loading = false;
        if (grid) grid.removeAttribute('aria-busy');
        cb(null, data);
      })
      .catch(function (err) {
        _loading = false;
        if (grid) grid.removeAttribute('aria-busy');
        cb(err, null);
      });
  }

  // ── Build a gallery card DOM node (no innerHTML with API data) ────────────────
  function buildCard(build) {
    var v      = build.vehicle || {};
    var wheels = build.wheels  || [];
    var w      = wheels[0]     || {};

    var title = [v.year, v.make, v.model].filter(Boolean).join(' ') || 'Customer Build';

    var specParts = [];
    if (w.vendor)                       specParts.push(w.vendor);
    if (w.wheel_model)                  specParts.push(w.wheel_model);
    if (w.diameter && w.width)          specParts.push(w.diameter + 'x' + w.width + '"');
    if (w.finish)                       specParts.push(w.finish);
    var spec = specParts.join(' · ');

    var href = safeBuildHref(build.entry_url, build.public_id);
    if (!href) return null; // skip card if no safe href

    var article = document.createElement('article');
    article.className = 'gi-card';
    if (build.public_id) article.setAttribute('data-public-id', build.public_id);

    var a = document.createElement('a');
    a.href      = href;
    a.className = 'gi-card-link';
    a.setAttribute('aria-label', title);

    // Image wrapper
    var imgWrap = document.createElement('div');
    imgWrap.className = 'gi-card-img-wrap';

    var rawSrc = build.primary_image_url;
    var safeSrc = safeImgUrl(rawSrc);

    if (safeSrc) {
      var img = document.createElement('img');
      var sep400 = safeSrc.includes('?') ? '&' : '?';
      var src400 = safeSrc + sep400 + 'width=400';
      var src600 = safeSrc + sep400 + 'width=600';
      img.src      = src600;
      img.srcset   = src400 + ' 400w, ' + src600 + ' 600w';
      img.sizes    = '(max-width:600px) 100vw, 33vw';
      img.alt      = title;               // title built from textContent-safe parts
      img.loading  = 'lazy';
      img.width    = 600;
      img.className = 'gi-card-img';
      imgWrap.appendChild(img);
    } else {
      var placeholder = document.createElement('div');
      placeholder.className = 'gi-card-img-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      imgWrap.appendChild(placeholder);
    }

    // Body
    var body = document.createElement('div');
    body.className = 'gi-card-body';

    var h3 = document.createElement('h3');
    h3.className = 'gi-card-title';
    h3.textContent = title;
    body.appendChild(h3);

    if (spec) {
      var p = document.createElement('p');
      p.className   = 'gi-card-spec';
      p.textContent = spec;
      body.appendChild(p);
    }

    a.appendChild(imgWrap);
    a.appendChild(body);
    article.appendChild(a);
    return article;
  }

  // ── Status node helpers (safe static strings only) ────────────────────────────
  function makeStatusNode(cls, message, linkHref, linkText) {
    var div = document.createElement('div');
    div.className = cls;
    var p = document.createElement('p');
    p.textContent = message;
    if (linkHref && linkText) {
      p.textContent = message + ' ';
      var a = document.createElement('a');
      a.href        = linkHref;
      a.textContent = linkText;
      p.appendChild(a);
    }
    div.appendChild(p);
    return div;
  }

  // ── Render grid from API response ─────────────────────────────────────────────
  function renderGrid(data) {
    if (!grid) return;
    _currentTotal = parseInt(data.total, 10) || 0;
    _currentPage  = parseInt(data.page,  10) || 1;

    // Clear existing content
    while (grid.firstChild) grid.removeChild(grid.firstChild);

    if (!data.data || data.data.length === 0) {
      grid.appendChild(makeStatusNode(
        'gi-empty',
        'No builds found matching your filters.',
        '/pages/share-your-build',
        'Be the first to share yours!'
      ));
    } else {
      var frag = document.createDocumentFragment();
      data.data.forEach(function (build) {
        var card = buildCard(build);
        if (card) frag.appendChild(card);
      });
      grid.appendChild(frag);
    }

    renderPagination();
  }

  // ── Render pagination (page numbers are integers — safe) ──────────────────────
  function renderPagination() {
    if (!pagination) return;
    var totalPages = Math.ceil(_currentTotal / PAGE_LIMIT);

    while (pagination.firstChild) pagination.removeChild(pagination.firstChild);
    if (totalPages <= 1) return;

    var pager = document.createElement('div');
    pager.className = 'gi-pager';

    function makePageBtn(pageNum, label) {
      var btn = document.createElement('button');
      btn.type      = 'button';
      btn.className = 'gi-pager-btn' + (pageNum === _currentPage ? ' gi-pager-btn--active' : '');
      btn.setAttribute('data-page', pageNum);
      btn.textContent = label !== undefined ? label : String(pageNum);
      btn.addEventListener('click', function () { goToPage(pageNum); });
      return btn;
    }

    if (_currentPage > 1) {
      pager.appendChild(makePageBtn(_currentPage - 1, '‹ Prev'));
    }

    var start = Math.max(1, _currentPage - 2);
    var end   = Math.min(totalPages, _currentPage + 2);
    for (var pg = start; pg <= end; pg++) {
      pager.appendChild(makePageBtn(pg));
    }

    if (_currentPage < totalPages) {
      pager.appendChild(makePageBtn(_currentPage + 1, 'Next ›'));
    }

    var info = document.createElement('span');
    info.className   = 'gi-pager-info';
    info.textContent = _currentTotal + ' build' + (_currentTotal !== 1 ? 's' : '');
    pager.appendChild(info);

    pagination.appendChild(pager);
  }

  // ── Push URL querystring ──────────────────────────────────────────────────────
  function pushQS(params) {
    var p = Object.assign({}, params);
    if (p.page === 1) delete p.page;
    delete p.limit;
    var qs = Object.keys(p)
      .filter(function (k) { return p[k] !== '' && p[k] !== undefined; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(p[k]); })
      .join('&');
    var url = window.location.pathname + (qs ? '?' + qs : '');
    window.history.pushState({ galleryParams: p }, '', url);
  }

  // ── Run a filter request ──────────────────────────────────────────────────────
  function applyFilters(page) {
    if (_loading) return;
    _currentPage = page || 1;
    var params = buildParams(_currentPage);
    pushQS(params);

    // Show loading state via safe DOM
    if (grid) {
      while (grid.firstChild) grid.removeChild(grid.firstChild);
      grid.appendChild(makeStatusNode('gi-loading', 'Loading…'));
    }

    fetchBuilds(params, function (err, data) {
      if (err) {
        if (grid) {
          while (grid.firstChild) grid.removeChild(grid.firstChild);
          grid.appendChild(makeStatusNode('gi-error', 'Could not load builds. Please try again.'));
        }
        return;
      }
      if (data && data.success === false) {
        if (grid) {
          while (grid.firstChild) grid.removeChild(grid.firstChild);
          grid.appendChild(makeStatusNode('gi-error', 'Gallery temporarily unavailable. Please try again.'));
        }
        return;
      }
      renderGrid(data);
    });
  }

  function goToPage(p) {
    applyFilters(p);
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Event listeners ───────────────────────────────────────────────────────────

  // Year change -> load makes, reset model
  if (inputs.year) {
    inputs.year.addEventListener('change', function () {
      var year = inputs.year.value;
      resetSelect(inputs.model, 'Any model', true);
      loadMakes(year, null);
    });
  }

  // Make change -> load models
  if (inputs.make) {
    inputs.make.addEventListener('change', function () {
      var year = inputs.year ? inputs.year.value : '';
      var make = inputs.make.value;
      loadModels(year, make, null);
    });
  }

  if (filterBtn) {
    filterBtn.addEventListener('click', function () { applyFilters(1); });
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      // Reset fitment (static) directly
      if (inputs.fitment) inputs.fitment.value = '';
      // Reset year then cascade
      if (inputs.year) inputs.year.value = '';
      resetSelect(inputs.make,  'Any make',  true);
      resetSelect(inputs.model, 'Any model', true);
      // Reset facet selects back to "Any"
      if (inputs.diameter)    inputs.diameter.value    = '';
      if (inputs.wheel_brand) inputs.wheel_brand.value = '';
      if (inputs.wheel_model) inputs.wheel_model.value = '';
      applyFilters(1);
    });
  }

  // Enter key on the static fitment select fires filter
  if (inputs.fitment) {
    inputs.fitment.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') applyFilters(1);
    });
  }

  window.addEventListener('popstate', function () {
    // Re-init dropdowns from new QS state
    var params = new URLSearchParams(window.location.search);
    _currentPage = parseInt(params.get('page'), 10) || 1;
    if (inputs.fitment) inputs.fitment.value = params.get('fitment') || '';
    // Cascade year/make/model
    var qYear  = params.get('year')  || '';
    var qMake  = params.get('make')  || '';
    var qModel = params.get('model') || '';
    if (qYear && inputs.year) inputs.year.value = qYear;
    loadMakes(qYear, qMake);
    if (qMake) loadModels(qYear, qMake, qModel);
    applyFilters(_currentPage);
  });

  // ── Boot ──────────────────────────────────────────────────────────────────────
  var qsParams = readQS();

  // Capture QS values for restoration after async loads
  var initYear       = qsParams.get('year')        || '';
  var initMake       = qsParams.get('make')        || '';
  var initModel      = qsParams.get('model')       || '';
  var initDiameter   = qsParams.get('diameter')    || '';
  var initWheelBrand = qsParams.get('wheel_brand') || '';
  var initWheelModel = qsParams.get('wheel_model') || '';

  // Load years (restoring QS year and cascading to makes/models if present)
  loadYears(initYear);
  if (initYear) {
    loadMakes(initYear, initMake);
    if (initMake) {
      loadModels(initYear, initMake, initModel);
    }
  }

  // Load facets
  loadFacets(initDiameter, initWheelBrand, initWheelModel);

  // Grid population
  var ssrCards = grid ? grid.querySelectorAll('.gi-card') : [];
  if (ssrCards.length === 0) {
    // No SSR content — JS must populate the grid entirely.
    applyFilters(_currentPage);
  } else {
    // SSR cards are present (Liquid rendered them).
    var hasFilters = ['year','make','model','wheel_brand','wheel_model','diameter','fitment'].some(function (k) {
      return !!qsParams.get(k);
    });
    if (hasFilters) {
      // Active filter params — replace SSR content with filtered results.
      applyFilters(_currentPage);
    } else {
      // No active filters but we still need pagination metadata.
      // Fetch page 1 silently; if the response arrives, update pagination controls
      // without discarding the SSR grid that crawlers already received.
      var pageParams = buildParams(1);
      fetchBuilds(pageParams, function (err, data) {
        if (err || !data) return;
        _currentTotal = parseInt(data.total, 10) || 0;
        _currentPage  = parseInt(data.page,  10) || 1;
        renderPagination();
      });
    }
  }

})();
